import { X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogClose,
} from "@/components/ui/dialog";

export type FreightDetail = {
  id: number;
  booking: string | null;
  carrier: string | null;
  origin: string | null;
  destination: string | null;
  container: string | null;
  etd: string | null;
  eta: string | null;
  status: string | null;
  vendor: string | null;
};

const STATUS_BADGE: Record<string, string> = {
  pending: "bg-slate-100 text-slate-700 border-slate-200",
  booked: "bg-blue-50 text-blue-700 border-blue-200",
  confirmed: "bg-blue-50 text-blue-700 border-blue-200",
  in_transit: "bg-cyan-50 text-cyan-700 border-cyan-200",
  arrived: "bg-emerald-50 text-emerald-700 border-emerald-200",
  delivered: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
  delayed: "bg-red-50 text-red-700 border-red-200",
};

function formatDate(s: string | null): string {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function prettifyStatus(s: string | null): string {
  if (!s) return "—";
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function FreightDetailDialog({
  freight,
  alertType,
  open,
  onOpenChange,
}: {
  freight: FreightDetail | null;
  alertType: "etd" | "eta" | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  if (!freight) return null;

  const statusKey = (freight.status ?? "pending").toLowerCase();
  const badgeClass = STATUS_BADGE[statusKey] ?? STATUS_BADGE.pending;
  const alertVerb =
    alertType === "etd" ? "Departing" : alertType === "eta" ? "Arriving" : "";
  const alertDate =
    alertType === "etd" ? formatDate(freight.etd) : alertType === "eta" ? formatDate(freight.eta) : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="sm:max-w-2xl"
      >
        {/* Upper-left close button */}
        <DialogClose
          className="absolute top-3 left-3 rounded-md p-1.5 opacity-70 hover:opacity-100 hover:bg-muted transition-opacity focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
          <span className="sr-only">Close</span>
        </DialogClose>

        <div className="pl-6 pt-2">
          {/* Title row */}
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-xl font-semibold text-foreground tracking-tight">
              {freight.booking ?? "Untitled booking"}
            </h2>
            {alertType && (
              <span
                className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                  alertType === "etd"
                    ? "bg-blue-50 text-blue-700 border-blue-200"
                    : "bg-emerald-50 text-emerald-700 border-emerald-200"
                }`}
              >
                {alertType.toUpperCase()}
              </span>
            )}
          </div>
          {alertVerb && (
            <p className="text-sm text-muted-foreground mb-5">
              {alertVerb} {alertDate}
            </p>
          )}

          {/* Status / Carrier / Vendor */}
          <div className="grid grid-cols-3 gap-4 mb-5">
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                Status
              </p>
              <span
                className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full border ${badgeClass}`}
              >
                {prettifyStatus(freight.status)}
              </span>
            </div>
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                Carrier
              </p>
              <p className="text-sm font-medium text-foreground">
                {freight.carrier ?? "—"}
              </p>
            </div>
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                Vendor
              </p>
              <p className="text-sm font-medium text-foreground">
                {freight.vendor ?? "—"}
              </p>
            </div>
          </div>

          {/* Route */}
          <div className="bg-muted/30 rounded-lg p-4 mb-5">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              Route
            </p>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <p className="text-[10px] text-muted-foreground mb-0.5">From</p>
                <p className="text-sm font-medium text-foreground">
                  {freight.origin ?? "—"}
                </p>
              </div>
              <div className="text-muted-foreground text-lg leading-none">→</div>
              <div className="flex-1">
                <p className="text-[10px] text-muted-foreground mb-0.5">To</p>
                <p className="text-sm font-medium text-foreground">
                  {freight.destination ?? "—"}
                </p>
              </div>
            </div>
          </div>

          {/* Schedule */}
          <div className="grid grid-cols-2 gap-4 mb-5">
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                ETD (Departs)
              </p>
              <p className="text-sm font-medium text-blue-600">{formatDate(freight.etd)}</p>
            </div>
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                ETA (Arrives)
              </p>
              <p className="text-sm font-medium text-emerald-600">{formatDate(freight.eta)}</p>
            </div>
          </div>

          {/* Container */}
          <div>
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">
              Container
            </p>
            <p className="text-sm font-medium text-foreground">
              {freight.container ?? "—"}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
